import {
    BounceRate,
    ChangePercentage,
    DefaultCellRightAlign,
    IndexCell,
    PagesPerVisit,
    RankCell,
    TimeCell,
    TrafficShare,
    WebsiteTooltipTopCell,
} from "app/components/React/Table/cells";
import { AdsenseCell } from "app/components/React/Table/cells/AdsenseCell";
import { CategoryCell } from "app/components/React/Table/cells/category-cell/CategoryCell";
import { DefaultCellHeader, DefaultCellHeaderRightAlign, HeaderCellBlank } from "app/components/React/Table/headerCells";
import type { Translate } from "app/filters/i18nFilter";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import type { ICategoryLeadersTableSort } from "./CategoryLeadersTable.types";

const skipForMobile = new Set(["AvgVisitDuration", "PagesPerVisit", "BounceRate"]);
export const getColumnsConfig = (
    translate: Translate,
    sortedColumn: ICategoryLeadersTableSort,
    device: string,
    isTopLevelCategory: boolean = false,
) => {
    const columns = [
        {
            fixed: true,
            cellComponent: IndexCell,
            headerComponent: HeaderCellBlank,
            disableHeaderCellHover: true,
            sortable: false,
            isResizable: false,
            width: 65,
        },
        {
            field: "Domain",
            fixed: true,
            displayName: translate("category.leaders.table.column.domain"),
            type: "string",
            format: "None",
            sortable: true,
            cellComponent: WebsiteTooltipTopCell,
            headerComponent: DefaultCellHeader,
            showTotalCount: true,
            tooltip: translate("widget.table.tooltip.topsites.domain"),
            width: 173,
            isResizable: false,
        },
        {
            field: "Share",
            displayName: translate("category.leaders.table.column.trafficshare"),
            type: "string",
            format: "percentagesign",
            sortable: true,
            fixed: false,
            cellComponent: TrafficShare,
            headerComponent: DefaultCellHeader,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.share"),
            minWidth: 150,
            isResizable: false,
        },
        {
            field: "MoMChange",
            displayName: translate("category.leaders.table.column.change"),
            type: "double",
            format: "percentagesign",
            fixed: false,
            sortable: true,
            cellComponent: ChangePercentage,
            headerComponent: DefaultCellHeaderRightAlign,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.change"),
            width: 110,
            isResizable: true,
        },
        isTopLevelCategory && {
            field: "Category",
            displayName: translate("common.industry"),
            type: "string",
            format: "None",
            sortable: true,
            fixed: false,
            cellComponent: CategoryCell,
            headerComponent: DefaultCellHeader,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.website_rankings.industry"),
            width: 185,
            isResizable: true,
        },
        {
            field: "RootCategoryRank",
            displayName: translate("common.tables.columns.industry.rank.title"),
            type: "long",
            format: "swRank",
            sortable: true,
            fixed: false,
            cellComponent: RankCell,
            headerComponent: DefaultCellHeaderRightAlign,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.website_rankings.industry_rank"),
            width: 130,
            isResizable: true,
            inverted: true,
        },
        {
            field: "AvgMonthVisits",
            displayName: translate("category.leaders.table.column.monthlyvisits"),
            type: "double",
            format: "minVisitsAbbr",
            sortable: true,
            cellComponent: DefaultCellRightAlign,
            headerComponent: DefaultCellHeaderRightAlign,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.monthlyvisits"),
            width: 130,
            fixed: false,
            isResizable: true,
        },
        {
            field: "AvgVisitDuration",
            displayName: translate("category.leaders.table.column.visitduration"),
            type: "double",
            format: "number",
            sortable: true,
            cellComponent: TimeCell,
            headerComponent: DefaultCellHeaderRightAlign,
            showTotalCount: false,
            fixed: false,
            isResizable: true,
            tooltip: translate("widget.table.tooltip.topsites.avgvisitduration"),
            width: 130,
        },
        {
            field: "PagesPerVisit",
            displayName: translate("category.leaders.table.column.pagespervisits"),
            type: "double",
            format: "number",
            sortable: true,
            cellComponent: PagesPerVisit,
            headerComponent: DefaultCellHeaderRightAlign,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.ppv"),
            width: 110,
            isResizable: true,
            fixed: false,
        },
        {
            field: "BounceRate",
            displayName: translate("category.leaders.table.column.bouncerate"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            cellComponent: BounceRate,
            headerComponent: DefaultCellHeaderRightAlign,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.bouncerate"),
            width: 125,
            inverted: true,
            isResizable: true,
        },
        {
            field: "HasAdsense",
            displayName: translate("category.leaders.table.column.adsense"),
            tooltip: translate("widget.table.tooltip.topsitesextended.hasadsense"),
            type: "bool",
            format: "None",
            fixed: false,
            sortable: true,
            cellComponent: AdsenseCell,
            headerComponent: DefaultCellHeader,
            showTotalCount: false,
            isResizable: true,
            width: 95,
        },
    ];

    return columns
        .filter(Boolean)
        .filter((col) => device === devicesTypes.DESKTOP || !skipForMobile.has(col.field))
        .map((col) => {
            const isSorted = sortedColumn && col.field === sortedColumn.field;
            return {
                ...col,
                visible: true,
                isSorted,
                sortDirection: isSorted ? sortedColumn.sortDirection : "desc",
            };
        });
};

export const getTableOptions = (translate: Translate) => {
    return {
        noDataTitle: translate("global.nodata.notavilable"),
        noDataSubtitle: translate("global.nodata.notavilable.subtitle"),
    };
};
